import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaGoogle } from "react-icons/fa";
import { FiEye, FiEyeOff, FiCheckCircle, FiXCircle } from "react-icons/fi";
import AnimatedModal from "./AnimatedModal";
import { authAPI, securityAPI, companiesAPI } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';
import PhoneInputComponent from "./PhoneInput";

export default function RegisterScreen({ isOpen, onClose, onSwitchToLogin }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    companyName: "",
    companyType: "",
    password: "",
    confirmPassword: ""
  });

  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const { register: authRegister } = useAuth();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [companyExists, setCompanyExists] = useState(null); // null=unknown, true/false
  const [checkingCompany, setCheckingCompany] = useState(false);
  const companyDebounceRef = useRef(null);
  const companyRequestIdRef = useRef(0);
  const lastCompanyQueriedRef = useRef('');

  const emailValid = useMemo(() => {
    const v = (formData.email || '').trim();
    if (!v) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }, [formData.email]);

  const phoneValidation = useMemo(() => {
    const phone = formData.phoneNumber;
    if (!phone) return { valid: false, e164: null };
    try {
      const isValid = isValidPhoneNumber(phone);
      if (isValid) {
        return { valid: true, e164: phone };
      }
    } catch {}
    return { valid: false, e164: null };
  }, [formData.phoneNumber]);

  const handleGoogleRegister = () => {
    alert("Redirect to Google OAuth for Registration");
  };

  // Fetch password policy when password fields are revealed
  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        setPolicyLoading(true);
        const policy = await securityAPI.getPasswordPolicy();
        setPasswordPolicy(policy);
      } catch (e) {
        // silently ignore; policy is optional
        setPasswordPolicy(null);
      } finally {
        setPolicyLoading(false);
      }
    };
    if (showPasswordFields && passwordPolicy === null && !policyLoading) {
      fetchPolicy();
    }
  }, [showPasswordFields, passwordPolicy, policyLoading]);

  const passwordChecks = useMemo(() => {
    const pwd = formData.password || "";
    const confirm = formData.confirmPassword || "";
    const policy = passwordPolicy;
    const rules = [];
    if (!policy || policy.isEnabled === false) return rules;

    if (typeof policy.minLength === 'number' && policy.minLength > 0) {
      const ok = pwd.length >= policy.minLength;
      rules.push({ key: 'minLength', ok, label: `At least ${policy.minLength} characters`, detail: `${pwd.length}/${policy.minLength}` });
    }
    if (typeof policy.maxLength === 'number' && policy.maxLength > 0) {
      const ok = pwd.length <= policy.maxLength;
      rules.push({ key: 'maxLength', ok, label: `No more than ${policy.maxLength} characters`, detail: `${pwd.length}/${policy.maxLength}` });
    }
    if (typeof policy.minUppercaseCount === 'number' && policy.minUppercaseCount > 0) {
      const uppercaseCount = (pwd.match(/[A-Z]/g) || []).length;
      const ok = uppercaseCount >= policy.minUppercaseCount;
      rules.push({ key: 'uppercase', ok, label: `At least ${policy.minUppercaseCount} uppercase letter${policy.minUppercaseCount>1?'s':''}`, detail: `${uppercaseCount}/${policy.minUppercaseCount}` });
    }
    if (typeof policy.minNumberCount === 'number' && policy.minNumberCount > 0) {
      const numberCount = (pwd.match(/[0-9]/g) || []).length;
      const ok = numberCount >= policy.minNumberCount;
      rules.push({ key: 'number', ok, label: `At least ${policy.minNumberCount} number${policy.minNumberCount>1?'s':''}`, detail: `${numberCount}/${policy.minNumberCount}` });
    }
    if (typeof policy.minSpecialCount === 'number' && policy.minSpecialCount > 0) {
      const specialCount = (pwd.match(/[^A-Za-z0-9]/g) || []).length;
      const ok = specialCount >= policy.minSpecialCount;
      rules.push({ key: 'special', ok, label: `At least ${policy.minSpecialCount} special character${policy.minSpecialCount>1?'s':''}`, detail: `${specialCount}/${policy.minSpecialCount}` });
    }
    // Provide a match indicator for UX (not a policy rule)
    if (confirm.length > 0 || pwd.length > 0) {
      const ok = pwd.length > 0 && confirm === pwd;
      rules.push({ key: 'match', ok, label: `Passwords match` });
    }
    return rules;
  }, [formData.password, formData.confirmPassword, passwordPolicy]);

  const doesPasswordMeetPolicy = () => {
    if (!passwordPolicy || passwordPolicy.isEnabled === false) return true;
    const pwd = formData.password || "";
    const policy = passwordPolicy;
    if (typeof policy.minLength === 'number' && policy.minLength > 0 && pwd.length < policy.minLength) return false;
    if (typeof policy.maxLength === 'number' && policy.maxLength > 0 && pwd.length > policy.maxLength) return false;
    if (typeof policy.minUppercaseCount === 'number' && policy.minUppercaseCount > 0) {
      if ((pwd.match(/[A-Z]/g) || []).length < policy.minUppercaseCount) return false;
    }
    if (typeof policy.minNumberCount === 'number' && policy.minNumberCount > 0) {
      if ((pwd.match(/[0-9]/g) || []).length < policy.minNumberCount) return false;
    }
    if (typeof policy.minSpecialCount === 'number' && policy.minSpecialCount > 0) {
      if ((pwd.match(/[^A-Za-z0-9]/g) || []).length < policy.minSpecialCount) return false;
    }
    return true;
  };

  const handlePhoneChange = (phone) => {
    setFormData(prev => ({
      ...prev,
      phoneNumber: phone || ''
    }));
    setError(""); // Clear error when user types
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    // Enforce alphabet-only for first and last names (allow spaces)
    if (name === 'firstName' || name === 'lastName') {
      nextValue = nextValue.replace(/[^A-Za-z\s]/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
    setError(""); // Clear error when user types

    // Show password fields after email is entered
    if (name === "email" && value.includes("@") && !showPasswordFields) {
      setShowPasswordFields(true);
    }

    // Debounced company exists check
    if (name === 'companyName') {
      if (companyDebounceRef.current) {
        clearTimeout(companyDebounceRef.current);
      }
      const trimmed = (nextValue || '').trim();
      if (!trimmed || trimmed.length < 2) {
        setCompanyExists(null);
        setCheckingCompany(false);
        return;
      }
      setCheckingCompany(true);
      companyRequestIdRef.current += 1;
      const requestId = companyRequestIdRef.current;
      lastCompanyQueriedRef.current = trimmed;
      companyDebounceRef.current = setTimeout(async () => {
        try {
          const res = await companiesAPI.existsByName(trimmed);
          if (requestId !== companyRequestIdRef.current) return;
          if (trimmed !== lastCompanyQueriedRef.current) return;
          setCompanyExists(!!res.exists);
        } catch (_) {
          if (requestId !== companyRequestIdRef.current) return;
          setCompanyExists(null);
        } finally {
          if (requestId === companyRequestIdRef.current) setCheckingCompany(false);
        }
      }, 400);
    }
  };

  const handleRegister = async () => {
    const { firstName, lastName, email, phoneNumber, companyName, companyType, password, confirmPassword } = formData;
    
    const requiresCompanyType = companyExists === false; // only new companies need type
    if (!firstName || !lastName || !email || !phoneNumber || !companyName || (requiresCompanyType && !companyType)) {
      setError("Please fill all required fields");
      return;
    }

    // Validate email format
    if (!emailValid) {
      setError("Please enter a valid email address");
      return;
    }

    // Validate first and last name alphabets only
    const nameRegex = /^[A-Za-z\s]+$/;
    if (!nameRegex.test(firstName) || !nameRegex.test(lastName)) {
      setError("First and Last name must contain letters only");
      return;
    }

    // Validate phone 
    if (!phoneValidation.valid) {
      setError("Please enter a valid phone number");
      return;
    }

    if (showPasswordFields && (!password || !confirmPassword)) {
      setError("Please enter and confirm your password");
      return;
    }

    if (showPasswordFields && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Enforce password policy if available and enabled
    if (!doesPasswordMeetPolicy()) {
      setError("Password does not meet the policy requirements.");
      return;
    }

    if (!agreedToTerms) {
      setError("Please agree to the Terms and Conditions");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const fullPhoneE164 = phoneValidation.e164; // already in +<code><number>
      // Extract country code from the E164 number
      const parsedPhone = parsePhoneNumberFromString(fullPhoneE164);
      const phoneCode = parsedPhone ? `+${parsedPhone.countryCallingCode}` : '';
      const phoneCountryCode = parsedPhone ? parsedPhone.country : '';
      
      const userData = {
        firstName,
        lastName,
        email,
        phoneNumber: fullPhoneE164,
        phoneCode,
        phoneCountry: phoneCountryCode,
        companyName,
        companyType,
        password
      };

      const response = await authAPI.register(userData);

      // Use auth context to manage registration
      authRegister(response);
      
      // Close the modal without showing alert
      onClose();
    } catch (error) {
      console.error("Registration failed:", error);
      setError("Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} className="w-full max-w-md">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg max-h-[90vh] overflow-y-auto relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl font-bold w-6 h-6 flex items-center justify-center z-10 transition-colors duration-200"
        >
          ×
        </button>
        
        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">Create Account</h2>

        {/* Google registration */}
        <button
          onClick={handleGoogleRegister}
          className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg font-medium mb-4 transition flex items-center justify-center gap-2"
        >
          <FaGoogle className="text-lg" />
          Continue with Google
        </button>

        <div className="flex items-center mb-4">
          <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
          <span className="mx-3 text-sm text-gray-500 dark:text-gray-400">or</span>
          <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
        </div>

        {/* Form fields */}
        <div className="space-y-3">
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="First Name *"
              className="p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Last Name *"
              className="p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          {/* Email */}
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Email Address *"
            className={`w-full p-3 rounded-lg border ${formData.email && !emailValid ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'} bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            required
          />
          {formData.email && !emailValid && (
            <div className="mt-1 text-xs text-red-600">Invalid email address</div>
          )}

          {/* Phone Number with integrated country code selection */}
          <div>
            <PhoneInputComponent
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              placeholder="Phone Number *"
              error={!phoneValidation.valid && formData.phoneNumber}
              className={phoneValidation.valid && formData.phoneNumber ? 'phone-input-valid' : ''}
            />
            {!phoneValidation.valid && formData.phoneNumber && (
              <div className="mt-1 text-xs text-red-600">Please enter a valid phone number</div>
            )}
          </div>

          {/* Company Name */}
          <div>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              placeholder="Company Name *"
              className={`w-full p-3 rounded-lg border ${companyExists === true ? 'border-yellow-400' : 'border-gray-300 dark:border-gray-600'} bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              required
            />
            {formData.companyName && (
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-300 flex items-center gap-2">
                {checkingCompany && <span className="animate-pulse">Checking…</span>}
                {!checkingCompany && companyExists === true && <span className="text-amber-600">Company exists</span>}
                {!checkingCompany && companyExists === false && <span className="text-green-600">Available</span>}
              </div>
            )}
          </div>

          {/* Company Type (only when company doesn't exist) */}
          {companyExists === false && (
            <select
              name="companyType"
              value={formData.companyType}
              onChange={handleInputChange}
              className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            >
              <option value="">Select Company Type *</option>
              <option value="contractor">Contractor</option>
              <option value="consultant">Consultant</option>
              <option value="government">Government</option>
              <option value="other">Other</option>
            </select>
          )}

          {/* Password fields (shown after email is entered) */}
          {showPasswordFields && (
            <>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Password *"
                  className="w-full p-3 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-300"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm Password *"
                  className="w-full p-3 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(v => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-300"
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              {passwordPolicy && passwordPolicy.isEnabled && (
                <div className="mt-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">Password requirements:</div>
                  <ul className="space-y-1 text-sm">
                    {passwordChecks.map(rule => (
                      <li key={rule.key} className={`flex items-center gap-2 ${rule.ok ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        {rule.ok ? <FiCheckCircle className="text-green-500" /> : <FiXCircle className="text-gray-400" />}
                        <span>
                          {rule.label}
                          {rule.detail ? ` (${rule.detail})` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-3 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Terms & Conditions agreement */}
        <div className="mt-4 flex items-start gap-2">
          <input
            id="agree-terms"
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <label htmlFor="agree-terms" className="text-sm text-gray-700 dark:text-gray-300">
            I agree to the{' '}
            <button type="button" onClick={() => setShowTerms(true)} className="text-indigo-600 hover:text-indigo-500 underline underline-offset-2">
              Terms and Conditions
            </button>
          </label>
        </div>

        {/* Register button */}
        <button
          onClick={handleRegister}
          disabled={isLoading}
          className={`w-full py-3 rounded-lg font-medium mt-6 transition ${
            isLoading 
              ? "bg-gray-400 cursor-not-allowed" 
              : "bg-indigo-600 hover:bg-indigo-500"
          } text-white`}
        >
          {isLoading ? "Creating Account..." : "Create Account"}
        </button>

        {/* Switch to login */}
        <div className="mt-4 text-center">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{" "}
            <button
              onClick={onSwitchToLogin}
              className="text-indigo-600 hover:text-indigo-500 font-medium transition-colors duration-200"
            >
              Login here
            </button>
          </span>
        </div>
      </div>
      {/* Terms Modal */}
      <AnimatedModal isOpen={showTerms} onClose={() => setShowTerms(false)} className="w-full max-w-3xl">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 p-0 overflow-hidden relative">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-500 p-5 flex items-center gap-3">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M5 11h14M5 19h14M7 7h.01M17 7h.01M7 19h.01M17 19h.01"/></svg>
            <h2 className="text-lg font-bold text-white">Terms and Conditions</h2>
            <button
              onClick={() => setShowTerms(false)}
              className="ml-auto text-white/90 hover:text-white text-2xl font-bold w-8 h-8 flex items-center justify-center"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="p-7 pt-6">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">Last Updated: {new Date().toLocaleDateString()}</div>
            <div className="max-h-[60vh] overflow-y-auto pr-1 hide-scrollbar">
              <div className="prose dark:prose-invert max-w-none text-sm">
                <p>Welcome to RAG AI (“we,” “our,” or “us”). These Terms and Conditions (“Terms”) govern your access to and use of our AI-powered Retrieval-Augmented Generation (RAG) application, website, APIs, and related services (collectively, the “Services”).</p>
                <p>By using the Services, you agree to be bound by these Terms. If you do not agree, you must stop using the Services immediately.</p>
                <h4>1. Definitions</h4>
                <p>“Services” – The RAG-based AI application, website, APIs, and related tools provided by RAG AI.</p>
                <p>“User” / “You” – Any individual or entity accessing or using the Services.</p>
                <p>“Content” – Any text, data, images, or other materials processed, generated, or uploaded to the Services.</p>
                <p>“Third-Party Data” – Information retrieved from external sources as part of the RAG process.</p>
                <h4>2. Eligibility</h4>
                <p>You must be at least 18 years old or have the consent of a legal guardian to use the Services. By using RAG AI, you confirm that you meet these eligibility requirements.</p>
                <h4>3. Account Registration</h4>
                <p>Some features require you to create an account. You are responsible for maintaining the confidentiality of your login credentials. You must notify us immediately of any unauthorized access or use of your account.</p>
                <h4>4. Use of the Services</h4>
                <ul className="list-disc pl-5">
                  <li>Use the Services only for lawful purposes.</li>
                  <li>Do not disrupt, damage, or impair the Services.</li>
                  <li>Do not upload malicious code, spam, or harmful content.</li>
                  <li>Do not attempt to reverse-engineer or exploit the Services without authorization.</li>
                </ul>
                <h4>5. AI and RAG Content Accuracy</h4>
                <p>RAG AI uses advanced Retrieval-Augmented Generation techniques to provide responses. No Guarantee of Accuracy – Responses may contain errors, outdated details, or incomplete information. You must independently verify any output before relying on it for critical decisions.</p>
                <h4>6. Third-Party Data and Links</h4>
                <p>RAG AI may retrieve and display data from third-party sources. We are not responsible for the accuracy, legality, or reliability of such sources. Links or references to external websites do not imply endorsement.</p>
                <h4>7. Intellectual Property</h4>
                <p>All intellectual property rights in RAG AI and the Services belong to us or our licensors. You retain ownership of the content you submit but grant us a non-exclusive, royalty-free, worldwide license to process it for providing the Services.</p>
                <h4>8. Prohibited Conduct</h4>
                <ul className="list-disc pl-5">
                  <li>Do not use the Services for illegal activities.</li>
                  <li>Do not infringe upon intellectual property rights.</li>
                  <li>Do not bypass security or access restrictions.</li>
                  <li>Do not automate usage in ways that overload or harm the system.</li>
                </ul>
                <h4>9. Privacy and Data Protection</h4>
                <p>Your use of RAG AI is also subject to our Privacy Policy, which explains how we collect, use, and protect your data.</p>
                <h4>10. Limitation of Liability</h4>
                <p>To the fullest extent permitted by law: We are not liable for indirect, incidental, or consequential damages. Our maximum liability shall not exceed the total amount you paid for the Services in the last [X] months.</p>
                <h4>11. Indemnification</h4>
                <p>You agree to indemnify and hold harmless RAG AI, its officers, employees, and partners from any claims, damages, or liabilities resulting from: Your use of the Services; Your violation of these Terms; Your infringement of third-party rights.</p>
                <h4>12. Termination</h4>
                <p>We may suspend or terminate your account at any time, without notice, if you breach these Terms.</p>
                <h4>13. Changes to the Terms</h4>
                <p>We may revise these Terms from time to time. The updated Terms will be posted on our website with a new “Last Updated” date. Continued use of the Services after changes means you accept the revised Terms.</p>
                <h4>14. Governing Law</h4>
                <p>These Terms are governed by the laws of your applicable jurisdiction, without regard to its conflict of laws principles.</p>
                <h4>15. Contact Information</h4>
                <p>RAG AI Support Team — Email: support@ragai.com</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setShowTerms(false)} className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700">Close</button>
              <button onClick={() => { setAgreedToTerms(true); setShowTerms(false); }} className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow">
                I Agree
              </button>
            </div>
          </div>
        </div>
      </AnimatedModal>
    </AnimatedModal>
  );
}
